import React from 'react'
import { useQuery } from '@apollo/react-hooks'
import { useTranslation } from 'react-i18next'
import { LoaderCircle } from 'lucide-react'
import moment from 'moment'
import Grid from '../../../components/grid'
import * as Query from '../../../api/gql/query'

interface SnagActivityLogProps {
  snagId: string
}

const SnagActivityLog: React.FC<SnagActivityLogProps> = ({ snagId }) => {
  const { t } = useTranslation()
  const {
    data: activityLogsData,
    loading,
    error,
  } = useQuery(Query.SNAG_ACTIVITY_LOGS, {
    variables: { snagId },
    skip: !snagId,
  })

  const activityLogs = activityLogsData?.snagActivityLogs || []

  const columns = [
    { title: t('Snags_activity_user_label'), name: 'user', width: 150 },
    { title: t('Snags_activity_action_label'), name: 'title', width: 200 },
    { title: t('Snags_activity_description_label'), name: 'description' },
    { title: t('Snags_activity_date_label'), name: 'createdAt', width: 160 },
  ]

  const rows =
    activityLogs?.map((log: any) => ({
      id: log.id,
      user: log.user?.name || t('Snags_activity_unknown_user'),
      title: log.title || '-',
      description: log.description || '-',
      createdAt: log.createdAt,
    })) || []

  const formatters = [
    {
      formatter: ({ value }: { value: string }) => {
        if (!value) return '-'
        return <span className="text-sm font-medium">{value}</span>
      },
      for: ['user'],
    },
    {
      formatter: ({ value }: { value: string }) => {
        if (!value) return '-'
        return <span className="text-sm font-medium">{value}</span>
      },
      for: ['title'],
    },
    {
      formatter: ({ value }: { value: string }) => {
        if (!value) return '-'
        return (
          <span className="text-sm text-muted-foreground break-words" title={value}>
            {value}
          </span>
        )
      },
      for: ['description'],
    },
    {
      formatter: ({ value }: { value: string }) => {
        if (!value) return '-'
        return <span className="text-sm">{moment(value).format('LLL')}</span>
      },
      for: ['createdAt'],
    },
  ]

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <LoaderCircle className="h-6 w-6 animate-spin text-primary" />
      </div>
    )
  }

  if (error) {
    return (
      <div className="flex items-center gap-2 rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-700">
        {t('Snags_activity_error')}: {error.message}
      </div>
    )
  }

  return (
    <div className="max-h-[400px] overflow-auto">
      <Grid
        columns={columns}
        rows={rows}
        formatters={formatters}
        showHeader
        sorting
        defaultSorting={[{ columnName: 'createdAt', direction: 'desc' }]}
        noDataMessage={t('Snags_activity_no_data')}
        showSelectionColumn={false}
        selectByRowClick={false}
      />
    </div>
  )
}

export default SnagActivityLog
